import React, { useState } from 'react'
import { Button, Group, IconButton, iconSizes, Paper, Stack } from '@common/ui'
import { IconCirclePlus, IconCircleMinus } from '@common/ui/icons'
import { com } from '@eidu/web'
import GroupHeader from './GroupHeader'
import EntitySelector = com.eidu.entity.selector.EntitySelector
import NonEmptyList = com.quickbirdstudios.nonEmptyCollection.list.NonEmptyList

import SelectorEditor from './SelectorEditor'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import { SelectorType } from './SelectorType'

type AndOrProps = {
  selector: EntitySelector.And | EntitySelector.Or
  entityTypes: readonly EntityType[]
  onChange: (newSelector: EntitySelector.And | EntitySelector.Or | EntitySelector.Not) => void
  selectorType: SelectorType
  showHeader?: boolean
  lockedEntityTypeId?: EntityTypeId
}

type KeyedSelector = {
  id: string
  selector: EntitySelector
}

const AndOrSelector: React.FC<AndOrProps> = ({
  selector,
  entityTypes,
  onChange,
  selectorType,
  showHeader,
  lockedEntityTypeId,
}) => {
  const operator = selector instanceof EntitySelector.And ? 'AND' : 'OR'
  const initialKeyed: KeyedSelector[] = selector.selectors
    .asJsReadonlyArrayView()
    .map((sel: EntitySelector) => ({ id: crypto.randomUUID(), selector: sel }))
  const [keyedChildren, setKeyedChildren] = useState<KeyedSelector[]>(initialKeyed)

  const updateChildren = (newKeyed: KeyedSelector[]) => {
    setKeyedChildren(newKeyed)
    const newChildren = NonEmptyList.fromArray(newKeyed.map((item) => item.selector))
    const newGroup = operator === 'AND' ? new EntitySelector.And(newChildren) : new EntitySelector.Or(newChildren)
    onChange(newGroup)
  }

  return (
    <Paper withBorder radius="sm" p="md">
      {showHeader && (
        <GroupHeader
          operator={operator}
          isNegated={false}
          onOperatorChange={(newOp) => {
            const newGroup =
              newOp === 'AND' ? new EntitySelector.And(selector.selectors) : new EntitySelector.Or(selector.selectors)
            onChange(newGroup)
          }}
          onToggleNegation={() => onChange(new EntitySelector.Not(selector))}
        />
      )}
      <Stack gap={0}>
        {keyedChildren.map((child) => (
          <Group key={child.id} gap={0} align="center" wrap="nowrap">
            <SelectorEditor
              selector={child.selector}
              onChange={(newSel) => {
                const newKeyed = keyedChildren.map((item) =>
                  item.id === child.id ? { ...item, selector: newSel } : item
                )
                updateChildren(newKeyed)
              }}
              entityTypes={entityTypes}
              selectorType={selectorType}
              lockedEntityTypeId={lockedEntityTypeId}
            />
            <IconButton
              aria-label="Remove entity type"
              onClick={() => {
                const newKeyed = keyedChildren.filter((item) => item.id !== child.id)
                updateChildren(newKeyed)
              }}
            >
              <IconCircleMinus size={iconSizes.sm} stroke={1.5} color="var(--trellis-color-negative)" />
            </IconButton>
          </Group>
        ))}
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconCirclePlus size={iconSizes.sm} stroke={1.5} />}
          onClick={() => {
            const newChild: KeyedSelector = {
              id: crypto.randomUUID(),
              selector: new EntitySelector.OfType(lockedEntityTypeId ?? entityTypes[0].id),
            }
            updateChildren([...keyedChildren, newChild])
          }}
          style={{ alignSelf: 'flex-start' }}
        >
          Add entity type
        </Button>
      </Stack>
    </Paper>
  )
}

export default AndOrSelector
